import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, getUser, BACKEND_URL } from "../api";
import { Avatar } from "./Posts";
import { formatProName } from "../lib/proName";

const eur = (c) => (c === 0 ? "Gratis" : c != null ? `€${(c / 100).toFixed(2)}` : "");
// Risolve il valore immagine (URL esterno, path /api relativo o id nudo) in un src caricabile
const resolveImg = (v) => !v ? "" : /^https?:\/\//i.test(v) ? v : v.startsWith("/") ? `${BACKEND_URL}${v}` : `${BACKEND_URL}/api/product-images/${v}`;
// Miniatura con fallback a placeholder neutro se assente o non caricabile
function Thumb({ src, className, phClassName }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return <div className={phClassName} data-testid="search-thumb-placeholder" />;
  return <img src={src} alt="" className={className} onError={() => setBroken(true)} data-testid="search-thumb-img" />;
}

// LIVELLO 1 — Ricerca rapida dalla lente: anteprima leggera nelle 5 categorie.
// INVIO o "Vedi tutti" -> pagina risultati completa /search (LIVELLO 2). Nessun filtro qui.
export function SearchPanel({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const me = getUser();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); setRes(null); setError(false); return; }
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    if (!open || !q.trim()) { setRes(null); setError(false); return; }
    setLoading(true); setError(false);
    const id = setTimeout(() => {
      api.get("/search-global", { params: { q: q.trim() } })
        .then(({ data }) => setRes(data))
        .catch(() => { setRes(null); setError(true); })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(id);
  }, [q, open, tick]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onPointer = (e) => { if (!e.target.closest("[data-search-root]")) onClose(); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [open, onClose]);

  if (!open) return null;

  const goResults = (type) => {
    const p = new URLSearchParams(); if (q.trim()) p.set("q", q.trim()); if (type) p.set("type", type);
    const qs = p.toString();
    onClose(); nav(qs ? `/search?${qs}` : "/search");
  };
  const onSubmit = (e) => { if (e.key === "Enter" && q.trim()) goResults(); };

  const totalCount = res ? (res.professionals.length + res.organizations.length + res.events.length + res.services.length + res.products.length) : 0;
  const section = (label) => (<div className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</div>);

  const body = (v) => (
    <>
      <div className="flex items-center gap-2 p-3 border-b border-slate-100 shrink-0">
        <input ref={inputRef} className="yp-input flex-1" value={q} onChange={e => setQ(e.target.value)} onKeyDown={onSubmit}
          placeholder={t("searchx.ph")} aria-label={t("searchx.title")} data-testid={`search-input-${v}`} />
        <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs shrink-0" onClick={onClose}
          aria-label={t("searchx.close")} data-testid={`search-close-${v}`}>✕</button>
      </div>
      <div className="max-h-[55vh] overflow-y-auto p-2 min-h-0 flex-1" data-testid={`search-results-${v}`}>
        {loading && <div className="text-sm text-slate-400 p-3" data-testid={`search-loading-${v}`}>{t("searchx.loading")}</div>}
        {!loading && error && (
          <div className="p-3 text-sm text-slate-500 flex items-center justify-between gap-2" data-testid={`search-error-${v}`}>
            <span>{t("searchx.error")}</span>
            <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs shrink-0" onClick={() => setTick(n => n + 1)} data-testid={`search-retry-${v}`}>{t("searchx.retry")}</button>
          </div>)}
        {!loading && !error && res !== null && (<>
          {totalCount === 0 && q.trim() && (
            <div className="text-sm text-slate-500 p-3" data-testid={`search-empty-${v}`}>{t("searchx.empty")}</div>)}

          {res.professionals.length > 0 && (
            <div data-testid={`search-pros-${v}`}>
              {section(t("searchx.pros"))}
              {res.professionals.map(p => (
                <Link key={p.id} to={`/professionals/${p.id}`} onClick={onClose}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`search-pro-result-${v}`}>
                  <Avatar url={p.avatar_url} label={p.first_name} cls="w-9 h-9 text-sm" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{formatProName({ title: p.professional_title, first_name: p.first_name, last_name: p.last_name }, i18n.language)}</div>
                    <div className="text-xs text-slate-500 truncate">{p.specialization || ""}</div>
                  </div>
                </Link>))}
              <button onClick={() => goResults("professionals")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-all-pros-${v}`}>{t("searchx.all_pros")}</button>
            </div>)}

          {res.organizations.length > 0 && (
            <div className="mt-2" data-testid={`search-orgs-${v}`}>
              {section(t("searchx.orgs"))}
              {res.organizations.map(o => (
                <Link key={o.id} to={`/organizations/${o.id}`} onClick={onClose}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`search-org-result-${v}`}>
                  <Avatar url={o.avatar_url} label={o.org_name} org cls="w-9 h-9 text-sm" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{o.org_name}</div>
                    <div className="text-xs text-slate-500 truncate">{[o.city, o.country].filter(Boolean).join(" · ")}</div>
                  </div>
                </Link>))}
              <button onClick={() => goResults("organizations")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-all-orgs-${v}`}>{t("searchx.all_orgs")}</button>
            </div>)}

          {res.events.length > 0 && (
            <div className="mt-2" data-testid={`search-events-${v}`}>
              {section(t("searchx.events"))}
              {res.events.map(e => (
                <Link key={e.id} to={`/events/${e.id}`} onClick={onClose}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`search-event-result-${v}`}>
                  <Thumb src={resolveImg(e.organization_avatar_url) || resolveImg(e.image_url)} className="w-9 h-9 rounded-lg object-cover shrink-0" phClassName="w-9 h-9 rounded-lg bg-slate-100 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{e.title}</div>
                    <div className="text-xs text-slate-500 truncate">{[e.organizer_name, e.start_at ? new Date(e.start_at).toLocaleDateString(i18n.language) : null].filter(Boolean).join(" · ")}</div>
                  </div>
                </Link>))}
              <button onClick={() => goResults("events")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-all-events-${v}`}>{t("searchx.all_events")}</button>
            </div>)}

          {res.services.length > 0 && (
            <div className="mt-2" data-testid={`search-services-${v}`}>
              {section(t("searchx.services"))}
              {res.services.map(s => (
                <Link key={s.id} to={`/services/${s.id}`} onClick={onClose}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`search-service-result-${v}`}>
                  <Thumb src={resolveImg(s.org_avatar_url)} className="w-9 h-9 rounded-lg object-cover shrink-0" phClassName="w-9 h-9 rounded-lg bg-blue-50 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{s.name}</div>
                    <div className="text-xs text-slate-500 truncate">{[s.org_name, s.professional_name, eur(s.price_cents)].filter(Boolean).join(" · ")}</div>
                  </div>
                </Link>))}
              <button onClick={() => goResults("services")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-all-services-${v}`}>{t("searchx.all_services")}</button>
            </div>)}

          {res.products.length > 0 && (
            <div className="mt-2" data-testid={`search-products-${v}`}>
              {section(t("searchx.products"))}
              {res.products.map(p => (
                <Link key={p.id} to={`/products/${p.id}`} onClick={onClose}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`search-product-result-${v}`}>
                  <Thumb src={resolveImg(p.image_id)} className="w-9 h-9 rounded-lg object-cover shrink-0" phClassName="w-9 h-9 rounded-lg bg-slate-100 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{[p.org_name, eur(p.price_cents), p.out_of_stock ? t("shop.out_of_stock") : null].filter(Boolean).join(" · ")}</div>
                  </div>
                </Link>))}
              <button onClick={() => goResults("products")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-all-products-${v}`}>{t("searchx.all_products")}</button>
            </div>)}

          {q.trim() && totalCount > 0 && (
            <button onClick={() => goResults()} className="block w-full text-center px-2 py-2 mt-2 text-sm font-semibold text-blue-600 hover:underline border-t border-slate-100" data-testid={`search-see-all-${v}`}>{t("searchpage.see_all")}</button>)}
        </>)}
        {!loading && !error && res === null && (
          <div className="p-2 space-y-1" data-testid={`search-browse-${v}`}>
            <button onClick={() => goResults("professionals")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-browse-pros-${v}`}>{t("searchx.all_pros")}</button>
            <button onClick={() => goResults("organizations")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-browse-orgs-${v}`}>{t("searchx.all_orgs")}</button>
            <button onClick={() => goResults("events")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-browse-events-${v}`}>{t("searchx.all_events")}</button>
            <button onClick={() => goResults("services")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-browse-services-${v}`}>{t("searchx.all_services")}</button>
            <button onClick={() => goResults("products")} className="block w-full text-left px-2 py-2 text-sm text-blue-600 hover:underline" data-testid={`search-browse-products-${v}`}>{t("searchx.all_products")}</button>
            <button onClick={() => goResults()} className="block w-full text-left px-2 py-2 mt-1 pt-2 text-sm font-semibold text-blue-600 hover:underline border-t border-slate-100" data-testid={`search-browse-all-${v}`}>{t("searchpage.see_all")}</button>
          </div>)}
      </div>
    </>);

  return (<>
    <div className="hidden lg:block absolute top-full right-0 mt-2 w-96 yp-card shadow-xl z-50 overflow-hidden" data-search-root data-testid="search-panel-desktop">{body("d")}</div>
    {createPortal(
      <div className="lg:hidden fixed inset-x-0 z-[60] yp-card !rounded-b-none shadow-xl overflow-hidden flex flex-col"
        style={{ bottom: "calc(57px + env(safe-area-inset-bottom, 0px))", maxHeight: "calc(100dvh - 73px - env(safe-area-inset-bottom, 0px))" }}
        data-search-root data-testid="search-panel-mobile">{body("m")}</div>,
      document.body)}
  </>);
}
